import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const VisionMissionContainer = styled.div`
  padding: 140px 0 60px;
  
  @media (max-width: 768px) {
    padding: 100px 0 40px;
  }
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #0a1628, #1a2744);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  height: 600px;
  border-radius: 25px;
  overflow: hidden;
  margin-bottom: 120px;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://images.unsplash.com/photo-1559666126-84f389727b9a?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80') no-repeat center center;
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(10, 22, 40, 0.8), rgba(26, 39, 68, 0.6));
  }
  
  &:hover::before {
    transform: scale(1.05);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  color: white;
  max-width: 800px;
  
  h2 {
    font-size: 4rem;
    
    margin-bottom: 25px;
    letter-spacing: -2px;
  
    color: #ffffff;}
  
  p {
    font-size: 1.4rem;
    
    line-height: 1.6;
    opacity: 0.95;
  
    color: #ffffff;}
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

// Vision Mission Cards
const VisionMissionSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .vm-grid .vm-card:nth-child(1) { animation: ${fadeInLeft} 0.8s ease-out 0.2s both; }
    .vm-grid .vm-card:nth-child(2) { animation: ${fadeInRight} 0.8s ease-out 0.4s both; }
  }
`;

const VMGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 80px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const VMCard = styled.div`
  position: relative;
  padding: 80px 60px;
  background: linear-gradient(135deg, ${props => props.type === 'vision' ? '#0a1628' : '#1a2744'} 0%, ${props => props.type === 'vision' ? '#1a2744' : '#0a1628'} 100%);
  border-radius: 25px;
  color: white;
  overflow: hidden;
  transition: all 0.4s ease;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-15px);
    box-shadow: 0 30px 60px rgba(10, 22, 40, 0.3);
  }
  
  .icon {
    font-size: 5rem;
    margin-bottom: 40px;
    opacity: 0.9;
    transition: all 0.4s ease;
  }
  
  &:hover .icon {
    transform: scale(1.1);
  }
  
  h3 {
    font-size: 3rem;
    font-weight: 400;
    margin-bottom: 30px;
    letter-spacing: -1.5px;
    color: #ffffff;
  }
  
  p {
    font-size: 1.3rem;
    line-height: 1.8;
    
    opacity: 0.95;
    color: #ffffff;
  }
  
  @media (max-width: 768px) {
    padding: 50px 40px;
    
    h3 {
      font-size: 2.2rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

// Strategy Section
const StrategySection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .section-title {
      animation: ${fadeInUp} 0.8s ease-out;
    }
    
    .strategy-grid .strategy-item:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.2s both; }
    .strategy-grid .strategy-item:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.4s both; }
    .strategy-grid .strategy-item:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.6s both; }
    .strategy-grid .strategy-item:nth-child(4) { animation: ${scaleIn} 0.6s ease-out 0.8s both; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 80px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const StrategyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 50px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const StrategyItem = styled.div`
  background: white;
  padding: 50px 40px;
  border-radius: 20px;
  border: 1px solid rgba(10, 22, 40, 0.08);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #0a1628, #1a2744);
    transform: scaleY(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(10, 22, 40, 0.12);
    border-color: rgba(10, 22, 40, 0.15);
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
  
  .icon {
    font-size: 3.5rem;
    color: #0a1628;
    margin-bottom: 30px;
    transition: all 0.4s ease;
  }
  
  &:hover .icon {
    transform: scale(1.1);
  }
  
  h3 {
    font-size: 1.8rem;
    color: #2c2c2c;
    margin-bottom: 20px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #6c757d;
    line-height: 1.7;
    font-size: 1.1rem;
    
  }
`;

// Goals Section
const GoalsSection = styled.section`
  background: linear-gradient(135f, #f8f9fa 0%, #ffffff 100%);
  padding: 100px 0;
  border-radius: 30px;
  margin-bottom: 120px;
  
  &.animate-in {
    .section-title {
      animation: ${fadeInUp} 0.8s ease-out;
    }
    
    .goals-list .goal-item:nth-child(1) { animation: ${fadeInLeft} 0.6s ease-out 0.2s both; }
    .goals-list .goal-item:nth-child(2) { animation: ${fadeInLeft} 0.6s ease-out 0.4s both; }
    .goals-list .goal-item:nth-child(3) { animation: ${fadeInLeft} 0.6s ease-out 0.6s both; }
    .goals-list .goal-item:nth-child(4) { animation: ${fadeInLeft} 0.6s ease-out 0.8s both; }
    .goals-list .goal-item:nth-child(5) { animation: ${fadeInLeft} 0.6s ease-out 1s both; }
  }
`;

const GoalsList = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const GoalItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 30px;
  margin-bottom: 40px;
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateX(10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  .number {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #0a1628, #1a2744);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 600;
    color: white;
    flex-shrink: 0;
  }
  
  .content {
    h3 {
      font-size: 1.6rem;
      color: #2c2c2c;
      margin-bottom: 10px;
      font-weight: 500;
    }
    
    p {
      color: #6c757d;
      line-height: 1.7;
      
    }
  }
`;

// Quote Section
const QuoteSection = styled.section`
  background: linear-gradient(135deg, #0a1628 0%, #1a2744 100%);
  padding: 100px 0;
  border-radius: 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  .quote-icon {
    font-size: 4rem;
    color: rgba(255, 255, 255, 0.3);
    margin-bottom: 30px;
  }
  
  blockquote {
    font-size: 2.5rem;
    
    color: white;
    line-height: 1.4;
    margin-bottom: 30px;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    letter-spacing: -1.5px;
  }
  
  .author {
    font-size: 1.2rem;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    blockquote {
      font-size: 1.8rem;
    }
  }
`;

const VisionMission = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <VisionMissionContainer>
      <Container>
        <PageTitle>{t('visionMission.title')}</PageTitle>
        <PageSubtitle>
          {t('visionMission.subtitle')}
        </PageSubtitle>

        <HeroSection 
          id="hero-section" 
          data-animate
          className={animatedSections.has('hero-section') ? 'animate-in' : ''}
        >
          <HeroContent>
            <h2>{t('visionMission.heroTitle')}</h2>
            <p>
              {t('visionMission.heroDesc')}
            </p>
          </HeroContent>
        </HeroSection>

        <VisionMissionSection 
          id="vm-section" 
          data-animate
          className={animatedSections.has('vm-section') ? 'animate-in' : ''}
        >
          <VMGrid className="vm-grid">
            <VMCard className="vm-card" type="vision">
              <div className="icon">🎯</div>
              <h3>{t('visionMission.visionTitle')}</h3>
              <p>
                {t('visionMission.visionDesc')}
              </p>
            </VMCard>

            <VMCard className="vm-card" type="mission">
              <div className="icon">🚀</div>
              <h3>{t('visionMission.missionTitle')}</h3>
              <p>
                {t('visionMission.missionDesc')}
              </p>
            </VMCard>
          </VMGrid>
        </VisionMissionSection>

        <StrategySection 
          id="strategy-section" 
          data-animate
          className={animatedSections.has('strategy-section') ? 'animate-in' : ''}
        >
          <SectionTitle className="section-title">{t('visionMission.strategyTitle')}</SectionTitle>
          <StrategyGrid className="strategy-grid">
            <StrategyItem className="strategy-item">
              <div className="icon">💡</div>
              <h3>{t('visionMission.str1Title')}</h3>
              <p>
                {t('visionMission.str1Desc')}
              </p>
            </StrategyItem>

            <StrategyItem className="strategy-item">
              <div className="icon">🌱</div>
              <h3>{t('visionMission.str2Title')}</h3>
              <p>
                {t('visionMission.str2Desc')}
              </p>
            </StrategyItem>

            <StrategyItem className="strategy-item">
              <div className="icon">🤝</div>
              <h3>{t('visionMission.str3Title')}</h3>
              <p>
                {t('visionMission.str3Desc')}
              </p>
            </StrategyItem>

            <StrategyItem className="strategy-item">
              <div className="icon">⭐</div>
              <h3>{t('visionMission.str4Title')}</h3>
              <p>
                {t('visionMission.str4Desc')}
              </p>
            </StrategyItem>
          </StrategyGrid>
        </StrategySection>

        <GoalsSection 
          id="goals-section" 
          data-animate
          className={animatedSections.has('goals-section') ? 'animate-in' : ''}
        >
          <SectionTitle className="section-title">{t('visionMission.goalsTitle')}</SectionTitle>
          <GoalsList className="goals-list">
            <GoalItem className="goal-item">
              <div className="number">1</div>
              <div className="content">
                <h3>{t('visionMission.goal1Title')}</h3>
                <p>{t('visionMission.goal1Desc')}</p>
              </div>
            </GoalItem>

            <GoalItem className="goal-item">
              <div className="number">2</div>
              <div className="content">
                <h3>{t('visionMission.goal2Title')}</h3>
                <p>{t('visionMission.goal2Desc')}</p>
              </div>
            </GoalItem>

            <GoalItem className="goal-item">
              <div className="number">3</div>
              <div className="content">
                <h3>{t('visionMission.goal3Title')}</h3>
                <p>{t('visionMission.goal3Desc')}</p>
              </div>
            </GoalItem>

            <GoalItem className="goal-item">
              <div className="number">4</div>
              <div className="content">
                <h3>{t('visionMission.goal4Title')}</h3>
                <p>{t('visionMission.goal4Desc')}</p>
              </div>
            </GoalItem>

            <GoalItem className="goal-item">
              <div className="number">5</div>
              <div className="content">
                <h3>{t('visionMission.goal5Title')}</h3>
                <p>{t('visionMission.goal5Desc')}</p>
              </div>
            </GoalItem>
          </GoalsList>
        </GoalsSection>

        <QuoteSection 
          id="quote-section" 
          data-animate
          className={animatedSections.has('quote-section') ? 'animate-in' : ''}
        >
          <div className="quote-icon">"</div>
          <blockquote>
            {t('visionMission.quote')}
          </blockquote>
          <div className="author">{t('visionMission.author')}</div>
        </QuoteSection>
      </Container>
    </VisionMissionContainer>
  );
};

export default VisionMission;